/**
 * i18next bootstrap. Loaded by main.tsx before the React tree mounts.
 *
 * Every catalog key is nested under `common` because all consumers call
 *   useTranslation('common'); t('formsList.title');
 * so the namespace stays flat and the language switcher only swaps resources.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const pt = {
  brand: 'Forms',
  login: { title: 'Entrar', submit: 'Entrar' },
  header: {
    toggleSidebar: 'Alternar menu',
    menu: 'Menu',
    enterFullscreen: 'Tela cheia',
    exitFullscreen: 'Sair da tela cheia',
    language: 'Idioma',
    theme: {
      title: 'Tema',
      light: 'Claro',
      dark: 'Escuro',
      system: 'Sistema',
    },
    notifications: 'Notificações',
    notificationsEmpty: 'Sem novidades',
    notificationsEmptyBody: 'Quando houver atualizações, elas aparecem aqui.',
    userMenu: 'Conta',
    profile: 'Perfil',
    logout: 'Sair',
  },
  actions: {
    add: 'Adicionar',
    save: 'Salvar',
    saved: 'Salvo',
    cancel: 'Cancelar',
    close: 'Fechar',
    edit: 'Editar',
    delete: 'Excluir',
    duplicate: 'Duplicar',
    remove: 'Remover',
    back: 'Voltar',
    next: 'Avançar',
    previous: 'Anterior',
    confirm: 'Confirmar',
    apply: 'Aplicar',
    reset: 'Restaurar',
    preview: 'Visualizar',
    publish: 'Publicar',
    published: 'Publicado',
    settings: 'Configurações',
    undo: 'Desfazer',
    redo: 'Refazer',
    copy: 'Copiar',
    copied: 'Copiado',
    moveUp: 'Mover para cima',
    moveDown: 'Mover para baixo',
  },
  forms: {
    title: 'Formulários',
    create: 'Novo formulário',
    empty: 'Nenhum formulário ainda',
    delete: { confirm: 'Excluir formulário?' },
  },
  formsList: {
    title: 'Formulários',
    description:
      'Crie formulários elegantes com editor centralizado, preview ao vivo e uma experiência pública mais limpa.',
    badge: 'AIDA Forms',
    newForm: 'Criar formulário',
    searchPlaceholder: 'Buscar por título, slug ou descrição...',
    counts: {
      forms: 'formulários',
      form: 'formulário',
      visible: 'visíveis',
    },
    status: { ready: 'Pronto' },
    actions: {
      edit: 'Editar',
      duplicate: 'Duplicar',
      delete: 'Excluir',
      openLive: 'Abrir público',
      copyUrl: 'Copiar URL',
      copySuffix: 'Cópia',
    },
    metrics: {
      fields: 'Campos',
      updated: 'Atualizado',
    },
    toasts: {
      loadError: 'Erro ao carregar formulários',
      deleteSuccess: 'Formulário excluído',
      deleteError: 'Erro ao excluir formulário',
      duplicateError: 'Erro ao duplicar formulário',
      urlCopied: 'URL copiada',
    },
    empty: {
      title: 'Nenhum formulário criado',
      description:
        'Crie seu primeiro formulário para começar a coletar respostas com um layout padronizado.',
      searchTitle: 'Nenhum formulário encontrado',
      searchDescription: 'Tente buscar com termos diferentes.',
    },
    confirm: {
      deleteTitle: 'Excluir formulário?',
      deleteMessage: 'Tem certeza que deseja excluir "{{name}}"?',
    },
    noDescription: 'Sem descrição definida.',
  },
  formBuilder: {
    defaults: {
      formTitle: 'Formulário sem título',
      newFieldLabel: 'Pergunta de {{type}}',
      submitButton: 'Enviar',
      successMessage: 'Formulário enviado com sucesso!',
      optionOne: 'Opção 1',
      optionTwo: 'Opção 2',
      optionThree: 'Opção 3',
    },
    palette: {
      badge: 'Blocos de pergunta',
      description:
        'Arraste os blocos para a tela do formulário e monte um fluxo com o visual AIDA.',
      tipSelect: 'Clique em um cartão para editar o conteúdo',
      tipReorder: 'Arraste os cartões para reordenar o formulário',
      tipRemove: 'Use duplicar e excluir em cada cartão',
      types: {
        text: 'Texto curto',
        email: 'E-mail',
        number: 'Número',
        textarea: 'Parágrafo',
        select: 'Lista suspensa',
        checkbox: 'Caixas de seleção',
        radio: 'Múltipla escolha',
        date: 'Data',
        file: 'Upload de arquivo',
      },
    },
    canvas: {
      selectOption: 'Selecione uma opção',
      conditional: 'Condicional',
      noPlaceholder: 'Sem texto de apoio ainda',
      duplicate: 'Duplicar',
      delete: 'Excluir',
      emptyTitle: 'Comece seu formulário',
      dropHint: 'Arraste blocos da paleta para cá',
    },
    actions: {
      backToForms: 'Voltar para Formulários',
      copyCreated: 'Cópia criada no editor',
      publishSuccess: 'Formulário publicado',
      saveSuccess: 'Formulário salvo',
    },
    preview: {
      title: 'Visualizar formulário',
      desktop: 'Computador',
      tablet: 'Tablet',
      mobile: 'Celular',
    },
    field: {
      label: 'Rótulo',
      placeholder: 'Texto de apoio',
      required: 'Obrigatório',
      options: 'Opções',
      addOption: 'Adicionar opção',
      helpText: 'Texto de ajuda',
      validation: 'Validação',
      logic: 'Lógica',
    },
    settings: {
      title: 'Configurações do formulário',
      general: 'Geral',
      appearance: 'Aparência',
      submit: 'Envio',
      formTitle: 'Título do formulário',
      formDescription: 'Descrição',
      submitButtonText: 'Texto do botão de envio',
      successMessage: 'Mensagem de sucesso',
      redirectUrl: 'URL de redirecionamento',
    },
    tabs: {
      questions: 'Perguntas',
      responses: 'Respostas',
      settings: 'Configurações',
    },
  },
  publicForm: {
    notFoundTitle: 'Formulário não encontrado',
    notFoundDescription: 'Este link é inválido ou o formulário não está mais disponível.',
    loadError: 'Erro ao carregar o formulário',
    requiredField: '"{{field}}" é obrigatório',
    alreadySubmitted: 'Você já respondeu a este formulário.',
    submitting: 'Enviando...',
    submitError: 'Erro ao enviar o formulário',
    successMessage: 'Formulário enviado com sucesso!',
    redirecting: 'Redirecionando...',
    sendAnotherResponse: 'Enviar outra resposta',
    closingTab: 'Fechando a aba...',
    closeBlocked: 'Não foi possível fechar a aba automaticamente.',
    closeTabNow: 'Fechar aba agora',
    stepOf: 'Etapa {{current}} de {{total}}',
    previous: 'Anterior',
    next: 'Avançar',
    emailField: {
      label: 'E-mail',
      placeholder: 'seu@email.com',
    },
  },
};

const en: typeof pt = {
  ...pt,
  login: { title: 'Sign in', submit: 'Sign in' },
  header: {
    ...pt.header,
    toggleSidebar: 'Toggle menu',
    menu: 'Menu',
    enterFullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    language: 'Language',
    theme: { title: 'Theme', light: 'Light', dark: 'Dark', system: 'System' },
    notifications: 'Notifications',
    notificationsEmpty: 'Nothing new',
    notificationsEmptyBody: 'Updates will show up here.',
    userMenu: 'Account',
    profile: 'Profile',
    logout: 'Sign out',
  },
  actions: {
    add: 'Add',
    save: 'Save',
    saved: 'Saved',
    cancel: 'Cancel',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    duplicate: 'Duplicate',
    remove: 'Remove',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    confirm: 'Confirm',
    apply: 'Apply',
    reset: 'Reset',
    preview: 'Preview',
    publish: 'Publish',
    published: 'Published',
    settings: 'Settings',
    undo: 'Undo',
    redo: 'Redo',
    copy: 'Copy',
    copied: 'Copied',
    moveUp: 'Move up',
    moveDown: 'Move down',
  },
  forms: {
    title: 'Forms',
    create: 'New form',
    empty: 'No forms yet',
    delete: { confirm: 'Delete form?' },
  },
  formsList: {
    title: 'Forms',
    description:
      'Build polished forms with a focused editor, live preview and a cleaner public experience.',
    badge: 'AIDA Forms',
    newForm: 'Create form',
    searchPlaceholder: 'Search by title, slug or description...',
    counts: { forms: 'forms', form: 'form', visible: 'visible' },
    status: { ready: 'Ready' },
    actions: {
      edit: 'Edit',
      duplicate: 'Duplicate',
      delete: 'Delete',
      openLive: 'Open public',
      copyUrl: 'Copy URL',
      copySuffix: 'Copy',
    },
    metrics: { fields: 'Fields', updated: 'Updated' },
    toasts: {
      loadError: 'Failed to load forms',
      deleteSuccess: 'Form deleted',
      deleteError: 'Failed to delete form',
      duplicateError: 'Failed to duplicate form',
      urlCopied: 'URL copied',
    },
    empty: {
      title: 'No form created yet',
      description: 'Create your first form to start collecting responses with a standard layout.',
      searchTitle: 'No form found',
      searchDescription: 'Try searching with different terms.',
    },
    confirm: {
      deleteTitle: 'Delete form?',
      deleteMessage: 'Are you sure you want to delete "{{name}}"?',
    },
    noDescription: 'No description set.',
  },
  formBuilder: {
    ...pt.formBuilder,
    tabs: {
      questions: 'Questions',
      responses: 'Responses',
      settings: 'Settings',
    },
    defaults: {
      formTitle: 'Untitled form',
      newFieldLabel: '{{type}} question',
      submitButton: 'Submit',
      successMessage: 'Form submitted successfully!',
      optionOne: 'Option 1',
      optionTwo: 'Option 2',
      optionThree: 'Option 3',
    },
    palette: {
      badge: 'Question blocks',
      description: 'Drag blocks to the form canvas and compose a flow with the AIDA visual style.',
      tipSelect: 'Click a card to edit its content',
      tipReorder: 'Drag cards to reorder the form',
      tipRemove: 'Use duplicate and delete on each card',
      types: {
        text: 'Short text',
        email: 'E-mail',
        number: 'Number',
        textarea: 'Paragraph',
        select: 'Dropdown',
        checkbox: 'Checkboxes',
        radio: 'Multiple choice',
        date: 'Date',
        file: 'File upload',
      },
    },
    canvas: {
      selectOption: 'Select an option',
      conditional: 'Conditional',
      noPlaceholder: 'No helper text yet',
      duplicate: 'Duplicate',
      delete: 'Delete',
      emptyTitle: 'Start your form',
      dropHint: 'Drag blocks from the palette here',
    },
    actions: {
      backToForms: 'Back to Forms',
      copyCreated: 'Copy created in the editor',
      publishSuccess: 'Form published',
      saveSuccess: 'Form saved',
    },
    preview: { title: 'Preview form', desktop: 'Desktop', tablet: 'Tablet', mobile: 'Mobile' },
    field: {
      label: 'Label',
      placeholder: 'Helper text',
      required: 'Required',
      options: 'Options',
      addOption: 'Add option',
      helpText: 'Help text',
      validation: 'Validation',
      logic: 'Logic',
    },
    settings: {
      title: 'Form settings',
      general: 'General',
      appearance: 'Appearance',
      submit: 'Submit',
      formTitle: 'Form title',
      formDescription: 'Description',
      submitButtonText: 'Submit button text',
      successMessage: 'Success message',
      redirectUrl: 'Redirect URL',
    },
  },
  publicForm: {
    notFoundTitle: 'Form not found',
    notFoundDescription: 'This link is invalid or the form is no longer available.',
    loadError: 'Failed to load the form',
    requiredField: '"{{field}}" is required',
    alreadySubmitted: 'You have already answered this form.',
    submitting: 'Submitting...',
    submitError: 'Failed to submit the form',
    successMessage: 'Form submitted successfully!',
    redirecting: 'Redirecting...',
    sendAnotherResponse: 'Send another response',
    closingTab: 'Closing tab...',
    closeBlocked: 'The tab could not be closed automatically.',
    closeTabNow: 'Close tab now',
    stepOf: 'Step {{current}} of {{total}}',
    previous: 'Previous',
    next: 'Next',
    emailField: {
      label: 'E-mail',
      placeholder: 'you@email.com',
    },
  },
};

const es: typeof pt = {
  ...pt,
  login: { title: 'Iniciar sesión', submit: 'Iniciar sesión' },
  header: {
    ...pt.header,
    toggleSidebar: 'Alternar menú',
    menu: 'Menú',
    enterFullscreen: 'Pantalla completa',
    exitFullscreen: 'Salir de pantalla completa',
    language: 'Idioma',
    theme: { title: 'Tema', light: 'Claro', dark: 'Oscuro', system: 'Sistema' },
    notifications: 'Notificaciones',
    notificationsEmpty: 'Sin novedades',
    notificationsEmptyBody: 'Las actualizaciones aparecerán aquí.',
    userMenu: 'Cuenta',
    profile: 'Perfil',
    logout: 'Salir',
  },
  actions: {
    add: 'Añadir',
    save: 'Guardar',
    saved: 'Guardado',
    cancel: 'Cancelar',
    close: 'Cerrar',
    edit: 'Editar',
    delete: 'Eliminar',
    duplicate: 'Duplicar',
    remove: 'Quitar',
    back: 'Volver',
    next: 'Siguiente',
    previous: 'Anterior',
    confirm: 'Confirmar',
    apply: 'Aplicar',
    reset: 'Restaurar',
    preview: 'Vista previa',
    publish: 'Publicar',
    published: 'Publicado',
    settings: 'Configuración',
    undo: 'Deshacer',
    redo: 'Rehacer',
    copy: 'Copiar',
    copied: 'Copiado',
    moveUp: 'Subir',
    moveDown: 'Bajar',
  },
  forms: {
    title: 'Formularios',
    create: 'Nuevo formulario',
    empty: 'Aún no hay formularios',
    delete: { confirm: '¿Eliminar formulario?' },
  },
  formsList: {
    title: 'Formularios',
    description:
      'Crea formularios elegantes con editor centralizado, vista previa en vivo y una experiencia pública más limpia.',
    badge: 'AIDA Forms',
    newForm: 'Crear formulario',
    searchPlaceholder: 'Buscar por título, slug o descripción...',
    counts: { forms: 'formularios', form: 'formulario', visible: 'visibles' },
    status: { ready: 'Listo' },
    actions: {
      edit: 'Editar',
      duplicate: 'Duplicar',
      delete: 'Eliminar',
      openLive: 'Abrir público',
      copyUrl: 'Copiar URL',
      copySuffix: 'Copia',
    },
    metrics: { fields: 'Campos', updated: 'Actualizado' },
    toasts: {
      loadError: 'Error al cargar formularios',
      deleteSuccess: 'Formulario eliminado',
      deleteError: 'Error al eliminar formulario',
      duplicateError: 'Error al duplicar formulario',
      urlCopied: 'URL copiada',
    },
    empty: {
      title: 'Ningún formulario creado',
      description: 'Crea tu primer formulario para empezar a recoger respuestas con un diseño estándar.',
      searchTitle: 'Ningún formulario encontrado',
      searchDescription: 'Prueba a buscar con otros términos.',
    },
    confirm: {
      deleteTitle: '¿Eliminar formulario?',
      deleteMessage: '¿Seguro que quieres eliminar "{{name}}"?',
    },
    noDescription: 'Sin descripción definida.',
  },
  formBuilder: {
    ...pt.formBuilder,
    tabs: {
      questions: 'Preguntas',
      responses: 'Respuestas',
      settings: 'Configuración',
    },
    defaults: {
      formTitle: 'Formulario sin título',
      newFieldLabel: 'Pregunta de {{type}}',
      submitButton: 'Enviar',
      successMessage: '¡Formulario enviado con éxito!',
      optionOne: 'Opción 1',
      optionTwo: 'Opción 2',
      optionThree: 'Opción 3',
    },
    palette: {
      badge: 'Bloques de pregunta',
      description: 'Arrastra los bloques al lienzo del formulario y compón un flujo con el estilo visual AIDA.',
      tipSelect: 'Haz clic en una tarjeta para editar su contenido',
      tipReorder: 'Arrastra las tarjetas para reordenar el formulario',
      tipRemove: 'Usa duplicar y eliminar en cada tarjeta',
      types: {
        text: 'Texto corto',
        email: 'Correo electrónico',
        number: 'Número',
        textarea: 'Párrafo',
        select: 'Lista desplegable',
        checkbox: 'Casillas',
        radio: 'Opción múltiple',
        date: 'Fecha',
        file: 'Subir archivo',
      },
    },
    canvas: {
      selectOption: 'Selecciona una opción',
      conditional: 'Condicional',
      noPlaceholder: 'Sin texto de apoyo aún',
      duplicate: 'Duplicar',
      delete: 'Eliminar',
      emptyTitle: 'Empieza tu formulario',
      dropHint: 'Arrastra bloques de la paleta aquí',
    },
    actions: {
      backToForms: 'Volver a Formularios',
      copyCreated: 'Copia creada en el editor',
      publishSuccess: 'Formulario publicado',
      saveSuccess: 'Formulario guardado',
    },
    preview: {
      title: 'Vista previa del formulario',
      desktop: 'Escritorio',
      tablet: 'Tableta',
      mobile: 'Móvil',
    },
    field: {
      label: 'Etiqueta',
      placeholder: 'Texto de apoyo',
      required: 'Obligatorio',
      options: 'Opciones',
      addOption: 'Añadir opción',
      helpText: 'Texto de ayuda',
      validation: 'Validación',
      logic: 'Lógica',
    },
    settings: {
      title: 'Configuración del formulario',
      general: 'General',
      appearance: 'Apariencia',
      submit: 'Envío',
      formTitle: 'Título del formulario',
      formDescription: 'Descripción',
      submitButtonText: 'Texto del botón de envío',
      successMessage: 'Mensaje de éxito',
      redirectUrl: 'URL de redirección',
    },
  },
  publicForm: {
    notFoundTitle: 'Formulario no encontrado',
    notFoundDescription: 'Este enlace no es válido o el formulario ya no está disponible.',
    loadError: 'Error al cargar el formulario',
    requiredField: '"{{field}}" es obligatorio',
    alreadySubmitted: 'Ya has respondido a este formulario.',
    submitting: 'Enviando...',
    submitError: 'Error al enviar el formulario',
    successMessage: '¡Formulario enviado con éxito!',
    redirecting: 'Redirigiendo...',
    sendAnotherResponse: 'Enviar otra respuesta',
    closingTab: 'Cerrando la pestaña...',
    closeBlocked: 'No se pudo cerrar la pestaña automáticamente.',
    closeTabNow: 'Cerrar pestaña ahora',
    stepOf: 'Paso {{current}} de {{total}}',
    previous: 'Anterior',
    next: 'Siguiente',
    emailField: {
      label: 'Correo electrónico',
      placeholder: 'tu@email.com',
    },
  },
};

const SUPPORTED_LNG = ['pt-BR', 'en-US', 'es'];
const STORAGE_KEY = 'mks_forms_lng';

function detectLanguage(): string {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LNG.includes(stored)) return stored;
  } catch {
    /* localStorage unavailable (SSR / private mode) */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'pt-BR';
  const exact = SUPPORTED_LNG.find((l) => l.toLowerCase() === nav.toLowerCase());
  if (exact) return exact;
  const base = nav.split('-')[0];
  const partial = SUPPORTED_LNG.find((l) => l.split('-')[0] === base);
  return partial ?? 'pt-BR';
}

i18n
  .use(initReactI18next)
  .init({
    lng: detectLanguage(),
    fallbackLng: 'pt-BR',
    supportedLngs: SUPPORTED_LNG,
    ns: ['common'],
    defaultNS: 'common',
    resources: {
      'pt-BR': { common: pt },
      'en-US': { common: en },
      es: { common: es },
    },
    interpolation: { escapeValue: false },
  })
  .catch(() => {
    /* i18next init failure is non-fatal; UI falls back to keys */
  });

i18n.on('languageChanged', (lng) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    /* persistence is best-effort */
  }
});

export default i18n;